import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Formik } from 'formik';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  Button,
  SecurityPreferencesEmail,
  SecurityPreferencesPassword,
  CreateTC,
  PersonalInfoPreferences,
  useTwoFactorVerificationModal,
} from '../../../components';
import {
  fetchTOAdminPreferences,
  TOAdminUpdateOwnOrganization,
  postTransportCompanyPricing,
  fetchTransportCompanyPricing,
  fetchTransportCompanyPricingHistory,
} from '../../../store/transport_organizations';
import {
  changePassword,
  changePasswordVerification,
  changeEmail,
  updatePersonalInformation,
} from '../../../store/authorization';
import { getAdditionalRideOptions } from '../../../store/preferences';
import { fetchStatesList } from '../../../store/common';
import {
  updatePasswordSchema,
  changeEmailSchema,
  GoogleAddressHelper,
  personalInformationSchema,
  LodashHelper,
} from '../../../utils/';
import { Divider, Timeline } from 'antd';
import { useCurrentRole } from '../../../hooks/useCurrentRole';
import { updateOrganizationSchema } from '../../../utils/validations';
import {
  handleAddCounties,
  handleCountiesSelect,
  handleDelete,
  handleStateSelect,
  getRegionFormFields,
  formatTransportCompanySubmit,
} from '../../../utils/countiesHelper';

import styles from './index.module.css';
import { TC_OPERATIONAL_HOURS, FLEET_OPTIONS, DEFAULT_TC_BILLING } from '../../../constants/constants';
import { TCBillSegment } from '../../../components/common/BillingAndInvoicing/TCBillSegment';
import { ConfirmChangePricingValuesModal } from '../../../components/modals/ConfirmPricingActionModals/ConfirmChangePricingValuesModal';

const PRICING_FIELD_LABELS = {
  flatRate: 'Ride Cost',
  additionalRate: 'Mileage Surcharge',
  rideNoShow: 'Rider No Show',
  sameDayCancellation: 'Same-Day Cancellation',
  waitTime: 'Wait Time (Per Minute)',
  deadheadMileage: 'Deadhead Mileage',
};

const HIDDEN_PRICING_FIELDS = ['pricingStatus', 'updatedById', 'isTest'];

const SUB_FEE_TYPES = ['ambulatory', 'stretcher', 'wheelchair'];

const getVisibleChanges = (changes) =>
  (changes || []).filter(
    (c) =>
      !HIDDEN_PRICING_FIELDS.includes(c.field) &&
      JSON.stringify(c.oldValue) !== JSON.stringify(c.newValue)
  );

const computeInsertDiff = (oldVal, newVal) => {
  if (!oldVal || !newVal) return [];
  return Object.keys(PRICING_FIELD_LABELS)
    .filter((field) => JSON.stringify(oldVal[field]) !== JSON.stringify(newVal[field]))
    .map((field) => ({ field, oldValue: oldVal[field], newValue: newVal[field] }));
};

const renderChangeCard = (change, idx) => {
  const label = PRICING_FIELD_LABELS[change.field] || change.field;
  const oldVal = change.oldValue;
  const newVal = change.newValue;
  const rows = [];

  if (newVal && typeof newVal === 'object' && !Array.isArray(newVal)) {
    const hasSubTypes = SUB_FEE_TYPES.some((k) => newVal[k] !== undefined);
    if (hasSubTypes) {
      SUB_FEE_TYPES.forEach((subType) => {
        if (newVal[subType] !== undefined) {
          const oldFee = oldVal?.[subType]?.fee;
          const newFee = newVal[subType]?.fee;
          const wasEnabled = oldVal?.[subType]?.enabled !== false;
          const isEnabled = newVal[subType]?.enabled !== false;
          const subLabel = subType.charAt(0).toUpperCase() + subType.slice(1);
          if (!isEnabled && wasEnabled) {
            rows.push({ label: subLabel, oldFee: `$${oldFee ?? newFee}`, newFee: 'Disabled' });
          } else if (isEnabled && !wasEnabled) {
            rows.push({ label: subLabel, oldFee: '$0', newFee: `$${newFee}` });
          } else if (oldFee !== newFee) {
            rows.push({ label: subLabel, oldFee: oldFee !== undefined ? `$${oldFee}` : null, newFee: `$${newFee}` });
          }
        }
      });
    } else if (newVal.fee !== undefined) {
      const oldFee = oldVal?.fee;
      const newFee = newVal.fee;
      const wasEnabled = oldVal?.enabled !== false;
      const isEnabled = newVal.enabled !== false;
      if (!isEnabled && wasEnabled) {
        rows.push({ label: 'Fee', oldFee: `$${oldFee ?? newFee}`, newFee: 'Disabled' });
      } else if (isEnabled && !wasEnabled) {
        rows.push({ label: 'Fee', oldFee: '$0', newFee: `$${newFee}` });
      } else if (oldFee !== newFee) {
        rows.push({ label: 'Fee', oldFee: oldFee !== undefined ? `$${oldFee}` : null, newFee: `$${newFee}` });
      }
    }
  } else if (newVal !== undefined && newVal !== null) {
    rows.push({
      label: 'Value',
      oldFee: oldVal !== undefined && String(oldVal) !== String(newVal) ? String(oldVal) : null,
      newFee: String(newVal),
    });
  }

  if (!rows.length) return null;

  return (
    <div key={idx} className={styles.changeCard}>
      <div className={styles.changeCardTitle}>{label.toUpperCase()}</div>
      <div className={styles.changeCardDivider} />
      {rows.map((row, ri) => (
        <div key={ri} className={styles.changeCardRow}>
          <span className={styles.changeCardSubLabel}>{row.label}</span>
          <span className={styles.changeCardValues}>
            {row.oldFee && (
              <>
                <span className={styles.changeCardOldVal}>{row.oldFee}</span>
                <span className={styles.changeCardArrow}>{' → '}</span>
              </>
            )}
            <span className={styles.changeCardNewVal}>{row.newFee}</span>
          </span>
        </div>
      ))}
    </div>
  );
};

const TC_Preferences = ({
  fetchTOAdminPreferences,
  TCPreferences,
  TOAdminUpdateOwnOrganization,
  postTransportCompanyPricing,
  fetchTransportCompanyPricing,
  fetchTransportCompanyPricingHistory,
  updatePersonalInformation,
  changePassword,
  changePasswordVerification,
  changeEmail,
  role,
  rideOptions,
  getAdditionalRideOptions,
  user,
  fetchStatesList,
  counties,
  cities,
  states,
  isViewDashboardMode,
  TCPricing,
  TCPricingHistory,
}) => {
  const { isEmployee, isTCWorker } = useCurrentRole(role);
  const canViewBilling = isViewDashboardMode;
  const securityRef = useRef(null);
  const { TwoFactorAuthenticationModal, twoFactorModalVisible, setTwoFactorModalVisible } =
    useTwoFactorVerificationModal({
      handleConfirm: (token, confirmCallback) =>
        changePasswordVerification({ token, newPassword: securityRef?.current?.values?.newPassword }).then((res) => {
          if (res) {
            confirmCallback();
            securityRef?.current?.resetForm();
          }
        }),
      email: user?.email,
    });

  const [currentPricing, setCurrentPricing] = useState();
  const [openPricingValuesModal, setOpenPricingValuesModal] = useState(false);
  const timelineScrollRef = useRef(null);
  const [thumbTop, setThumbTop] = useState(0);
  const [thumbVisible, setThumbVisible] = useState(false);

  const THUMB_HEIGHT = 50;

  const handleTimelineScroll = () => {
    const el = timelineScrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight) { setThumbVisible(false); return; }
    setThumbVisible(true);
    const maxTop = clientHeight - THUMB_HEIGHT;
    setThumbTop(Math.round((scrollTop / (scrollHeight - clientHeight)) * maxTop));
  };

  useEffect(() => { fetchTOAdminPreferences(); }, [fetchTOAdminPreferences]);

  useEffect(() => { fetchStatesList(); }, [fetchStatesList]);

  useEffect(() => { getAdditionalRideOptions(); }, [getAdditionalRideOptions]);

  useEffect(() => {
    if (TCPreferences?.tcPricingId) {
      const payload = {
        orgId: user?.organizationId,
        tcPricingId: TCPreferences?.tcPricingId
      }

      fetchTransportCompanyPricing(payload);
    }

  }, [fetchTransportCompanyPricing, TCPreferences?.tcPricingId, user?.organizationId]);

  useEffect(() => {
    if (canViewBilling && user?.organizationId) {
      fetchTransportCompanyPricingHistory({ orgId: user.organizationId });
    }
  }, [fetchTransportCompanyPricingHistory, canViewBilling, user?.organizationId]);

  useEffect(() => {
    if (TCPricing) {

      if (TCPricing?.data) {
        setCurrentPricing(TCPricing?.data[0]);
      } else {
        setCurrentPricing(TCPricing);
      }
    }
  }, [TCPricing]);

  const onSubmitPricing = (values) => {
    const isValuesChanged = !LodashHelper.isEqual(values, TCPricing);

    if (isValuesChanged) {
      setOpenPricingValuesModal(true);
      setCurrentPricing(values);
    }
  };

  const onConfirm = (values) => {
    postTransportCompanyPricing({ ...values, id: TCPricing?.id, organizationId: user?.organizationId });
    setOpenPricingValuesModal(false);
  }

  const handleSelectAddress = async ({ value, values, setValues }) => {
    setValues({ ...values, location: GoogleAddressHelper.transformFields(value) });
  };

  const handleChangeAddress = ({ value, values, setValues }) => {
    setValues({
      ...values,
      location: {
        formattedAddress: value,
      },
    });
  };

  const allowScrollToPersonalInfo = useMemo(() => {
    return !LodashHelper.isEmpty(TCPreferences) && !LodashHelper.isEmpty(user);
  }, [TCPreferences, user]);

  const sortedPricingHistory = useMemo(
    () =>
      [...(TCPricingHistory || [])].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      ),
    [TCPricingHistory]
  );

  useEffect(() => {
    const el = timelineScrollRef.current;
    if (!el) return;
    setThumbVisible(el.scrollHeight > el.clientHeight);
  }, [sortedPricingHistory]);

  return (
    <div className={styles.pageContent}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>Preferences</h1>
      </div>
      <div className={styles.pageSection}>
        <h2 className={styles.formHead}> Company Information</h2>

        <Formik
          enableReinitialize
          validateOnChange={false}
          validationSchema={updateOrganizationSchema}
          initialValues={
            TCPreferences
              ? {
                ...TCPreferences,
                fleetSizes: Array.isArray(TCPreferences.fleetSizes) ? TCPreferences.fleetSizes : FLEET_OPTIONS,
                hourOfOperations: TCPreferences?.hourOfOperations || TC_OPERATIONAL_HOURS,
                operationalCity: TCPreferences?.operationalCity || [],
                ...getRegionFormFields(
                  TCPreferences?.regionsOfOperation,
                  TCPreferences?.operationalCity,
                ),
              }
              : {
                name: '',
                regionsOfOperation: [],
                operationalCity: [],
                state: '',
                counties: [],
                cities: [],
                location: {
                  formattedAddress: '',
                },
                vehicleTypes: [],
                fleetSizes: FLEET_OPTIONS,
                hourOfOperations: TC_OPERATIONAL_HOURS,
                rideOptions: [],
              }
          }
          onSubmit={({ admin, counties, state, cities, ...values }) => TOAdminUpdateOwnOrganization(formatTransportCompanySubmit(values))}>
          {({ values, ...rest }) => {
            return (
              <form className={styles.form} onSubmit={rest.handleSubmit}>
                <CreateTC
                  handleChangeAddress={handleChangeAddress}
                  handleSelectAddress={handleSelectAddress}
                  showPersonalInfo={false}
                  hideUberVehicleOption
                  values={values}
                  counties={counties}
                  cities={cities}
                  states={states}
                  handleStateSelect={handleStateSelect}
                  handleCountiesSelect={handleCountiesSelect(counties)}
                  handleAddCounties={handleAddCounties({ values, setValues: rest.setValues })}
                  handleDelete={handleDelete}
                  rideOptions={rideOptions || []}
                  disableAllFields={(isEmployee && isTCWorker) || isViewDashboardMode}
                  {...rest}
                />
                {role && !((isEmployee && isTCWorker) || isViewDashboardMode) && (
                  <div className={styles.btnPanel}>
                    <div className={styles.btnControl}>
                      <Button disabled={!rest.isValid || isViewDashboardMode} type={'submit'}>
                        Save Changes
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            );
          }}
        </Formik>
      </div>
      {canViewBilling &&
        <div className={styles.pageSection}>
          <div className={styles.billingContainer}>
            <div className={styles.billingMain}>
              <TCBillSegment
                key="TCPricing"
                Default_Value={TCPricing ? currentPricing : DEFAULT_TC_BILLING}
                onSubmitPricing={onSubmitPricing}
              />
            </div>
            {sortedPricingHistory.length > 0 && (
              <>
                <Divider orientation="vertical" className={styles.billingDivider} />
                <div className={styles.billingPanel}>
                  <div className={styles.panelTitleWrapper}>
                    <h2 className={styles.panelTitle}>Billing Update Logs</h2>
                  </div>
                  <div className={styles.scrollbarWrapper}>
                    <div
                      ref={timelineScrollRef}
                      className={styles.timelineScroll}
                      onScroll={handleTimelineScroll}
                    >
                      <Timeline
                        mode="start"
                        items={(() => {
                          const UNSET = {};
                          let prevInsertValue = UNSET;
                          return [...(TCPricingHistory || [])]
                            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                            .map((entry) => {
                              if (entry?.action === 'INSERT') {
                                const curr = prevInsertValue;
                                prevInsertValue = entry.value;
                                if (curr === UNSET) return entry;
                                const diff = computeInsertDiff(curr, entry.value);
                                return diff.length ? { ...entry, action: 'UPDATE', changes: diff } : null;
                              }
                              return entry;
                            })
                            .filter((entry) => entry && (entry.action === 'INSERT' || getVisibleChanges(entry?.changes).length > 0))
                            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                        })().map((entry) => {
                          const visibleChanges = getVisibleChanges(entry?.changes);
                          const date = new Date(entry?.createdAt);
                          const dateStr = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' });
                          const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'America/Chicago' }) + ' CST';
                          const name = entry?.updatedBy
                            ? `${entry.updatedBy.firstName || ''} ${entry.updatedBy.lastName || ''}`.trim() || '--'
                            : '--';
                          return {
                            icon: <span className={styles.timelineDot} />,
                            title: (
                              <div className={styles.dateLabel}>
                                <div>{dateStr}</div>
                                <div>{timeStr}</div>
                              </div>
                            ),
                            content: (
                              <div>
                                <div className={styles.entryHeader}>
                                  <span className={entry?.action === 'INSERT' ? styles.badgeCreated : styles.badgeUpdated}>
                                    {entry?.action === 'INSERT' ? 'Created' : 'Updated'}
                                  </span>
                                  <span className={styles.entryName}>{name}</span>
                                </div>
                                {visibleChanges.length > 0 && (
                                  <div className={styles.changeCardsWrapper}>
                                    {visibleChanges.map(renderChangeCard)}
                                  </div>
                                )}
                              </div>
                            ),
                          };
                        })}
                      />
                    </div>
                    {thumbVisible && (
                      <div className={styles.scrollTrack}>
                        <div
                          className={styles.scrollThumb}
                          style={{ top: thumbTop, height: THUMB_HEIGHT }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {openPricingValuesModal &&
            ConfirmChangePricingValuesModal({
              type: "transport company",
              handleConfirm: () => onConfirm(currentPricing),
              handleCancel: () => setOpenPricingValuesModal(false),
            })}
        </div>
      }

      <div className={styles.pageSection}>
        <Formik
          validateOnMount
          enableReinitialize
          initialValues={
            TCPreferences && !(isEmployee && isTCWorker)
              ? {
                ...TCPreferences.admin,
              }
              : {
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                phoneNumber: user.phoneNumber || '',
                jobTitle: user.jobTitle || '',
                emailSubscription: user?.emailSubscription === null ? false : user?.emailSubscription,
                userRole: user?.role
              }
          }
          validationSchema={personalInformationSchema}
          onSubmit={({ firstName, lastName, phoneNumber, jobTitle, emailSubscription }) => {
            updatePersonalInformation(
              { firstName, lastName, phoneNumber, jobTitle, emailSubscription },
              'Successful! User preferences have been updated',
            );
          }}>
          {({ values, isValid, handleSubmit }) => {
            return (
              <form className={styles.form} onSubmit={handleSubmit}>
                <PersonalInfoPreferences
                  values={values}
                  hideTimezoneField
                  disableAllFields={isViewDashboardMode}
                  allowScrollToPersonsalInfo={allowScrollToPersonalInfo}
                />
                {!isViewDashboardMode && (
                  <div className={styles.btnPanel}>
                    <div className={styles.btnControl}>
                      <Button disabled={!isValid || isViewDashboardMode} type='submit'>
                        Save Changes
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            );
          }}
        </Formik>
      </div>
      <div>
        <h2>Password Information</h2>
        <Formik
          enableReinitialize
          validateOnMount
          initialValues={
            TCPreferences && !(isEmployee && isTCWorker)
              ? {
                newEmail: TCPreferences?.admin?.email || '',
                password: '',
              }
              : { newEmail: user?.email || '', password: '' }
          }
          onSubmit={(values) => changeEmail(values)}
          validationSchema={changeEmailSchema(isEmployee && isTCWorker ? user?.email : TCPreferences?.admin?.email)}>
          {({ values, ...rest }) => {
            return (
              <form className={styles.form} onSubmit={rest.handleSubmit}>
                {user && (
                  <SecurityPreferencesEmail
                    values={values}
                    initialEmail={isEmployee && isTCWorker ? user?.email : TCPreferences?.admin?.email}
                    disableAllFields={isViewDashboardMode}
                    {...rest}
                  />
                )}
              </form>
            );
          }}
        </Formik>
        <Formik
          innerRef={securityRef}
          validateOnMount
          initialValues={{
            newPassword: '',
            oldPassword: '',
          }}
          validationSchema={updatePasswordSchema}
          onSubmit={(values) => {
            changePassword(values).then((res) => setTwoFactorModalVisible(res));
          }}>
          {({ values, ...rest }) => {
            return (
              <form className={styles.form} onSubmit={rest.handleSubmit}>
                <SecurityPreferencesPassword values={values} disableAllFields={isViewDashboardMode} {...rest} />
              </form>
            );
          }}
        </Formik>
      </div>
      {twoFactorModalVisible && TwoFactorAuthenticationModal}
    </div>
  );
};

const mapStateToProps = (state) => ({
  TCPreferences: state.transportOrganizations.TCPreferences,
  rideOptions: state.preferences.rideOptions?.filter((option) => !option.disabled),
  user: state?.authorization?.user,
  counties: state.common.counties,
  cities: state.common.cities,
  states: state.common.states,
  isViewDashboardMode: state?.authorization?.viewDashboardMode?.active,
  TCPricing: state.transportOrganizations.TCPricing,
  TCPricingHistory: state.transportOrganizations.TCPricingHistory,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchTOAdminPreferences,
      TOAdminUpdateOwnOrganization,
      updatePersonalInformation,
      changePassword,
      changeEmail,
      getAdditionalRideOptions,
      fetchStatesList,
      changePasswordVerification,
      postTransportCompanyPricing,
      fetchTransportCompanyPricing,
      fetchTransportCompanyPricingHistory,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(TC_Preferences);
